import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CampaignIcon from "@mui/icons-material/Campaign";
import api from "../../api";
import ResponsiveTable from "../../components/ResponsiveTable";
import WaitingListCampaignModal from "../../components/WaitingListCampaignModal";

// Palette mirrors pages/Course/CourseList.jsx so the two pages read as one console.
const PAGE_BACKGROUND = "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)";
const HEADER_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const CARD_BACKGROUND = "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)";
const ROW_HOVER = "rgba(102, 126, 234, 0.05)";

const gradientButtonStyle = {
  background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
  color: "#fff",
  fontWeight: 600,
  paddingX: 2,
  paddingY: 0.5,
  borderRadius: 2,
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
    opacity: 0.9,
  },
  "&.Mui-disabled": {
    background: "#c7cad1",
    color: "#fff",
  },
};

const STATUS_OPTIONS = ["new", "contacted", "enrolled", "closed"];

const STATUS_COLORS = {
  new: "warning",
  contacted: "info",
  enrolled: "success",
  closed: "default",
};

const SOURCE_LABELS = {
  course_page: "Course page",
  register_interest: "Register interest",
};

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const CSV_COLUMNS = [
  { header: "ID", value: (r) => r.id ?? "" },
  { header: "Name", value: (r) => r.name ?? "" },
  { header: "Email", value: (r) => r.email ?? "" },
  { header: "Mobile", value: (r) => r.phone ?? "" },
  { header: "Child's Year", value: (r) => r.child_year ?? "" },
  { header: "Course", value: (r) => r.course?.name || r.course_name || "" },
  { header: "Target School", value: (r) => r.school?.name || r.school_name || "" },
  { header: "Source", value: (r) => SOURCE_LABELS[r.source] || r.source || "" },
  { header: "Status", value: (r) => r.status ?? "" },
  { header: "Message", value: (r) => r.message ?? "" },
  { header: "Submitted At", value: (r) => r.created_at ?? "" },
];

const buildCsv = (records) => {
  const headerLine = CSV_COLUMNS.map((col) => csvCell(col.header)).join(",");
  const rows = records.map((record) =>
    CSV_COLUMNS.map((col) => csvCell(col.value(record))).join(",")
  );
  return `﻿${[headerLine, ...rows].join("\r\n")}`;
};

const downloadCsvFile = (filename, csvText) => {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CourseWaitingList = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [campaignOpen, setCampaignOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");

  const buildParams = useCallback(
    (overrides = {}) => ({
      page: page + 1,
      per_page: rowsPerPage,
      search: searchTerm || undefined,
      status: status || undefined,
      source: source || undefined,
      ...overrides,
    }),
    [page, rowsPerPage, searchTerm, status, source]
  );

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("admin/course-waiting-lists", { params: buildParams() });
      setEntries(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotalRecords(res.data?.total || 0);
      setSelectedIds([]);
    } catch (err) {
      console.error("Error fetching waiting list", err);
      setError(err.response?.data?.message || "Failed to fetch the waiting list.");
      setEntries([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleStatusChange = async (entry, newStatus) => {
    try {
      await api.patch(`admin/course-waiting-lists/${entry.id}`, { status: newStatus });
      setEntries((prev) =>
        prev.map((row) => (row.id === entry.id ? { ...row, status: newStatus } : row))
      );
    } catch (err) {
      console.error("Error updating waiting list entry", err);
      setError(err.response?.data?.message || "Failed to update the entry.");
    }
  };

  // Export every matching row, not just the page on screen.
  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = [];
      let currentPage = 1;
      let lastPage = 1;
      do {
        // The API caps per_page at 200, so page through until every match is collected.
        const res = await api.get("admin/course-waiting-lists", {
          params: buildParams({ page: currentPage, per_page: 200 }),
        });
        if (Array.isArray(res.data?.data)) rows.push(...res.data.data);
        lastPage = res.data?.last_page || 1;
        currentPage += 1;
      } while (currentPage <= lastPage);

      const dateStamp = new Date().toISOString().slice(0, 10);
      downloadCsvFile(`course-waiting-list-${dateStamp}.csv`, buildCsv(rows));
    } catch (err) {
      console.error("Error exporting waiting list", err);
      setError(err.response?.data?.message || "Failed to export the waiting list.");
    } finally {
      setExporting(false);
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = (event) => {
    setSelectedIds(event.target.checked ? entries.map((e) => e.id) : []);
  };

  const selectedEntries = entries.filter((e) => selectedIds.includes(e.id));

  const resetToFirstPage = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  return (
    <Box sx={{ minHeight: "100vh", background: PAGE_BACKGROUND, p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ textAlign: "left", flex: 1, mr: { sm: 3 } }}>
          <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 1 }}>
            Course Waiting List
          </Typography>
          <Box sx={{ maxWidth: 420 }}>
            <Typography variant="body2" component="div" sx={{ color: "#5a6c7d", fontSize: "13px" }}>
              People who asked to be notified when a place opens up
            </Typography>
            <Typography
              variant="body1"
              component="div"
              sx={{ opacity: 0.9, mt: 1.5, color: "#5a6c7d", fontSize: "13px" }}
            >
              Total entries - {totalRecords} {searchTerm ? `| Entries found for "${searchTerm}"` : ""}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button
            sx={gradientButtonStyle}
            startIcon={<CampaignIcon />}
            onClick={() => setCampaignOpen(true)}
            disabled={selectedIds.length === 0}
          >
            {selectedIds.length > 0 ? `Share Course (${selectedIds.length})` : "Share Course"}
          </Button>
          <Button
            sx={gradientButtonStyle}
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || totalRecords === 0}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, background: CARD_BACKGROUND, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Search name, email, mobile, course or school"
              value={searchTerm}
              onChange={resetToFirstPage(setSearchTerm)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={resetToFirstPage(setStatus)}>
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select label="Source" value={source} onChange={resetToFirstPage(setSource)}>
                <MenuItem value="">All</MenuItem>
                {Object.keys(SOURCE_LABELS).map((key) => (
                  <MenuItem key={key} value={key}>
                    {SOURCE_LABELS[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ background: CARD_BACKGROUND, borderRadius: 2, overflow: "hidden" }}>
        <ResponsiveTable>
          <Table>
            <TableHead
              sx={{
                background: HEADER_GRADIENT,
                "& .MuiTableCell-head": {
                  background: HEADER_GRADIENT,
                  color: "white",
                  fontWeight: 600,
                },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < entries.length}
                    checked={entries.length > 0 && selectedIds.length === entries.length}
                    onChange={toggleAll}
                    disabled={entries.length === 0}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Child's Year</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Target School</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      No waiting list entries found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id} sx={{ "&:hover": { backgroundColor: ROW_HOVER } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(entry.id)}
                        onChange={() => toggleOne(entry.id)}
                      />
                    </TableCell>
                    <TableCell>{entry.name}</TableCell>
                    <TableCell>{entry.email}</TableCell>
                    <TableCell>{entry.phone || "—"}</TableCell>
                    <TableCell>{entry.child_year || "—"}</TableCell>
                    <TableCell>{entry.course?.name || entry.course_name || "—"}</TableCell>
                    <TableCell>{entry.school?.name || entry.school_name || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={SOURCE_LABELS[entry.source] || entry.source}
                        sx={{
                          background: "linear-gradient(45deg, #667eea, #764ba2)",
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(entry.created_at)}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={entry.status}
                        onChange={(e) => handleStatusChange(entry, e.target.value)}
                        renderValue={(value) => (
                          <Chip size="small" label={value} color={STATUS_COLORS[value] || "default"} />
                        )}
                        sx={{ minWidth: 130 }}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ResponsiveTable>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <WaitingListCampaignModal
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        selectedEntries={selectedEntries}
      />
    </Box>
  );
};

export default CourseWaitingList;
